import React, { useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import "../admin-ui.css";

type ImportSummary = {
  id: string;
  importId?: string;
  createdAt: string;
  sourceFileName: string;
  specialty: string;
  assignmentType?: "test" | "task";
  studentsCount: number;
  activeStudentsCount?: number;
  studentsWithResultsCount?: number;
  studentsWithoutResultsCount?: number;
  safeDeleteCount?: number;
  statusKey?: "active" | "old" | "legacy" | "unknown";
  statusLabel?: string;
};

type ImportStudent = {
  fullName: string;
  birthDate: string;
  specialty: string;
  username: string;
  password: string;
  role: string;
  importId?: string;
};

type ImportRecord = ImportSummary & {
  students: ImportStudent[];
};

type CleanupStudent = ImportStudent & {
  isActiveNow?: boolean;
  existsInUsers?: boolean;
  hasResult?: boolean;
  cleanupStatus?: {
    key: string;
    label: string;
    canAutoDelete: boolean;
  };
};

type ImportCleanupPreview = {
  id?: string;
  importId: string;
  importDate?: string;
  importFileName: string;
  specialty: string;
  assignmentType: "test" | "task";
  statusKey: "active" | "old" | "legacy" | "unknown";
  statusLabel: string;
  totalStudents: number;
  activeStudents: number;
  studentsWithResults: number;
  studentsWithoutResultsCount: number;
  missingInUsersCount: number;
  safeDeleteCount: number;
  skippedCount: number;
  students: CleanupStudent[];
};

const MOSCOW_TIME_ZONE = "Europe/Moscow";

const formatMoscowDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString("ru-RU", { timeZone: MOSCOW_TIME_ZONE })
    : "";

const getAssignmentLabel = (assignmentType?: string) =>
  assignmentType === "task" ? "Ситуационные задачи" : "Тестирование";

const getStatusClass = (statusKey?: string) => {
  if (statusKey === "active") return "admin-status-passed";
  if (statusKey === "old") return "admin-status-not-started";
  return "admin-status-incomplete";
};

const AdminImportsPage: React.FC = () => {
  const [imports, setImports] = useState<ImportSummary[]>([]);
  const [selectedImport, setSelectedImport] = useState<ImportRecord | null>(null);
  const [cleanupPreview, setCleanupPreview] = useState<ImportCleanupPreview | null>(null);
  const [message, setMessage] = useState("");
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const role = localStorage.getItem("role") || "";
  const username = localStorage.getItem("username") || "";

  const superadminHeaders = {
    "X-User-Role": role,
    "X-Username": username,
  };

  const loadImports = () => {
    fetch("/api/imports")
      .then((res) => res.json())
      .then((data: ImportSummary[]) => setImports(data))
      .catch((err) => console.error("Ошибка загрузки истории ведомостей:", err));
  };

  useEffect(() => {
    loadImports();
  }, []);

  const handleOpen = async (id: string) => {
    setMessage("");

    try {
      const response = await fetch(`/api/imports/${id}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось открыть ведомость");
      }

      setSelectedImport(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Не удалось открыть ведомость";
      setMessage(errorMessage);
    }
  };

  const handleExport = (id: string) => {
    window.location.href = `/api/imports/${id}/export`;
  };

  const handleAnalyzeImport = async (item: ImportSummary) => {
    setMessage("");

    try {
      const cleanupId = item.importId || item.id;
      const response = await fetch(`/api/imports/${cleanupId}/cleanup-preview`, {
        headers: superadminHeaders,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось выполнить анализ импорта");
      }

      setCleanupPreview(data);
      await handleOpen(item.id);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Не удалось выполнить анализ импорта";
      setMessage(errorMessage);
    }
  };

  const handleCleanupImport = async (item: ImportSummary) => {
    if (
      !window.confirm(
        "Удалить студентов из этого импорта? Активные студенты и студенты с результатами удалены не будут."
      )
    ) {
      return;
    }

    setMessage("");

    try {
      const cleanupId = item.importId || item.id;
      const response = await fetch(`/api/imports/${cleanupId}/cleanup`, {
        method: "DELETE",
        headers: superadminHeaders,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось очистить импорт");
      }

      setMessage(
        `Импорт очищен. Удалено: ${data.deletedCount || 0}. Пропущено: ${
          data.skippedCount || 0
        }.`
      );
      setCleanupPreview(data.preview || null);
      loadImports();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Не удалось очистить импорт";
      setMessage(errorMessage);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Удалить ведомость из истории?")) {
      return;
    }

    setMessage("");

    try {
      const response = await fetch(`/api/imports/${id}`, {
        method: "DELETE",
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось удалить ведомость");
      }

      if (selectedImport?.id === id) {
        setSelectedImport(null);
      }

      setMessage("Ведомость удалена");
      loadImports();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Не удалось удалить ведомость";
      setMessage(errorMessage);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Удалить все ведомости из истории?")) {
      return;
    }

    setIsDeletingAll(true);
    setMessage("");

    try {
      const response = await fetch("/api/imports", {
        method: "DELETE",
        headers: superadminHeaders,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Не удалось удалить все ведомости");
      }

      setSelectedImport(null);
      setCleanupPreview(null);
      setMessage(`Удалено ведомостей: ${data.deletedCount || 0}`);
      loadImports();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Не удалось удалить все ведомости";
      setMessage(errorMessage);
    } finally {
      setIsDeletingAll(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-inner">
        <AdminNav section="imports" />

        <h2>История ведомостей</h2>

        {message && <p className="admin-message">{message}</p>}

        <div className="admin-card admin-toolbar">
          <button
            className="admin-button admin-button-danger"
            type="button"
            onClick={handleDeleteAll}
            disabled={imports.length === 0 || isDeletingAll}
          >
            Удалить все ведомости
          </button>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Дата импорта</th>
                <th>Специальность</th>
                <th>Назначение</th>
                <th>Исходный файл</th>
                <th>Студентов</th>
                <th>Активны</th>
                <th>С результатами</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>

            <tbody>
              {imports.length === 0 ? (
                <tr>
                  <td colSpan={9} className="admin-table-empty">
                    Нет сохраненных ведомостей
                  </td>
                </tr>
              ) : (
                imports.map((item) => (
                  <tr key={item.id}>
                    <td>{formatMoscowDateTime(item.createdAt)}</td>
                    <td>{item.specialty}</td>
                    <td>
                      <span
                        className={`admin-badge ${
                          item.assignmentType === "task"
                            ? "admin-badge-task"
                            : "admin-badge-test"
                        }`}
                      >
                        {getAssignmentLabel(item.assignmentType)}
                      </span>
                    </td>
                    <td>{item.sourceFileName}</td>
                    <td>{item.studentsCount}</td>
                    <td>{item.activeStudentsCount ?? "—"}</td>
                    <td>{item.studentsWithResultsCount ?? "—"}</td>
                    <td>
                      <span className={`admin-badge ${getStatusClass(item.statusKey)}`}>
                        {item.statusLabel || "По запросу"}
                      </span>
                    </td>
                    <td>
                      <button
                        className="admin-button admin-button-small"
                        onClick={() => handleOpen(item.id)}
                      >
                        Открыть
                      </button>{" "}
                      <button
                        className="admin-button admin-button-small"
                        onClick={() => handleExport(item.id)}
                      >
                        Скачать
                      </button>{" "}
                      <button
                        className="admin-button admin-button-small"
                        onClick={() => handleAnalyzeImport(item)}
                      >
                        Анализ импорта
                      </button>{" "}
                      <button
                        className="admin-button admin-button-small admin-button-danger"
                        onClick={() => handleCleanupImport(item)}
                      >
                        Удалить этот импорт
                      </button>{" "}
                      <button
                        className="admin-button admin-button-small"
                        onClick={() => handleDelete(item.id)}
                      >
                        Удалить ведомость
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {cleanupPreview && (
          <section className="admin-card" style={{ marginTop: "20px" }}>
            <h3>
              Анализ импорта:{" "}
              {cleanupPreview.importFileName || cleanupPreview.importId}
            </h3>
            <div className="admin-summary-grid">
              <div className="admin-summary-card">
                <strong>{cleanupPreview.totalStudents}</strong>
                <span>Всего студентов</span>
              </div>
              <div className="admin-summary-card">
                <strong>{cleanupPreview.activeStudents}</strong>
                <span>Активны сейчас</span>
              </div>
              <div className="admin-summary-card">
                <strong>{cleanupPreview.studentsWithResults}</strong>
                <span>Имеют результаты</span>
              </div>
              <div className="admin-summary-card">
                <strong>{cleanupPreview.missingInUsersCount}</strong>
                <span>Отсутствуют в users.json</span>
              </div>
              <div className="admin-summary-card">
                <strong>{cleanupPreview.safeDeleteCount}</strong>
                <span>Можно удалить безопасно</span>
              </div>
              <div className="admin-summary-card">
                <strong>{cleanupPreview.skippedCount}</strong>
                <span>Будут пропущены</span>
              </div>
            </div>
            <p className="admin-muted">
              Активные студенты и студенты с результатами не удаляются обычной
              безопасной очисткой.
            </p>
          </section>
        )}

        {selectedImport && (
          <section className="admin-card" style={{ marginTop: "32px" }}>
            <h3>
              {selectedImport.specialty} — {selectedImport.sourceFileName}
            </h3>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ФИО</th>
                    <th>Дата рождения</th>
                    <th>Специальность</th>
                    <th>Логин</th>
                    <th>Пароль</th>
                  </tr>
                </thead>

                <tbody>
                  {selectedImport.students.map((student, index) => (
                    <tr key={`${student.username}-${index}`}>
                      <td>{student.fullName}</td>
                      <td>{student.birthDate}</td>
                      <td>{student.specialty}</td>
                      <td>{student.username}</td>
                      <td>{student.password}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminImportsPage;
